import React from 'react';

import {
  MdAssignment,
  MdCheckCircle,
  MdAutorenew,
  MdEdit,
  MdBarChart,
  MdPerson,
  MdSchool,
  MdTrendingUp,
} from 'react-icons/md';

import { Card, CardContent } from '../common/Card';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon:
    | 'assignment'
    | 'check'
    | 'progress'
    | 'draft'
    | 'chart'
    | 'person'
    | 'school'
    | 'trending';
  color:
    | 'gray'
    | 'green'
    | 'blue'
    | 'amber'
    | 'purple'
    | 'red'
    | 'indigo'
    | 'pink';
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
  className?: string;
}

const colorClasses = {
  gray: {
    text: 'text-gray-600',
    bg: 'bg-gray-50',
    iconBg: 'bg-gray-100',
  },
  green: {
    text: 'text-green-600',
    bg: 'bg-green-50',
    iconBg: 'bg-green-100',
  },
  blue: {
    text: 'text-blue-600',
    bg: 'bg-blue-50',
    iconBg: 'bg-blue-100',
  },
  amber: {
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    iconBg: 'bg-amber-100',
  },
  purple: {
    text: 'text-purple-600',
    bg: 'bg-purple-50',
    iconBg: 'bg-purple-100',
  },
  red: {
    text: 'text-red-600',
    bg: 'bg-red-50',
    iconBg: 'bg-red-100',
  },
  indigo: {
    text: 'text-indigo-600',
    bg: 'bg-indigo-50',
    iconBg: 'bg-indigo-100',
  },
  pink: {
    text: 'text-pink-600',
    bg: 'bg-pink-50',
    iconBg: 'bg-pink-100',
  },
};

const iconMap = {
  assignment: MdAssignment,
  check: MdCheckCircle,
  progress: MdAutorenew,
  draft: MdEdit,
  chart: MdBarChart,
  person: MdPerson,
  school: MdSchool,
  trending: MdTrendingUp,
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  subtitle,
  className = '',
}) => {
  const colorClass = colorClasses[color];
  const IconComponent = iconMap[icon];

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${className}`}
    >
      <CardContent className="p-2">
        <div className="space-y-4">
          {/* Icon at the top */}
          <div className="flex justify-between items-start">
            <div className={`p-2 rounded-lg ${colorClass.bg}`}>
              {React.createElement(IconComponent as React.ElementType, {
                className: `text-2xl ${colorClass.text}`,
              })}
            </div>
            {trend && (
              <span
                className={`text-sm font-semibold ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {trend.isPositive ? '↗' : '↘'} {Math.abs(trend.value)}%
              </span>
            )}
          </div>

          {/* Content */}
          <div className="space-y-1">
            <p className="text-sm md:text-base font-medium text-gray-600">
              {title}
            </p>
            <p className={`text-xl font-bold ${colorClass.text}`}>{value}</p>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatsCard;
